import { InspectionListItem } from "../_objects/inspection";
import { IndividualReportSchema } from "../_offline/models/individual-report.schema";

export enum InspectionReportPermission {
  Non, // 0 権限なし
  Read, // 1 参照
  Write // 2 編集
}

export enum ReportImageQuality {
  HIGH, // 0
  MEDIUM, // 1
  LOW // 2
}

export class InspectionReport {
  id: string;
  reportId: string;
  title: string;
  inspectionIds: string[];
  templateId: string;
  version: number;
  status: string;
  reportLocations: ReportLocation[];
  editableTexts: {};
  excludedInputGroups: string[];
  permission: number;
  isAutoInputTitle: boolean;

  toPDFInstanceId: string | null = null;
  schemaVersion: string;
  createdBy: string;
  createDate: string;
  lastUpdatedBy: string;
  lastUpdateDate: string;
  blobStorageAccount: string;
  lastCompletedDate: string;
  lastDraftDate: string;
  thumbFilePath: string;
  finalQuality?: ReportImageQuality;
  draftQuality?: ReportImageQuality;

  // Made static functions as in some cases generic JavaScript objects are
  // type-cast as InspectionReport without instanciating a real object of the class.
  public static getThumbnailFileName(version: number, addExtension: boolean = true): string {
    const extension = addExtension ? ".png" : "";
    return `thumbnail-${version}${extension}`;
  }

  public static getFinalReportTitle(title: string, version: number): string {
    if (version > 1) {
      title += `-${version}`;
    }
    return title;
  }

  public static getTitleBasedThumbnailFileName(title: string, version: number, addExtension: boolean = true): string {
    const extension = addExtension ? ".png" : "";
    return `${title}-${version}${extension}`;
  }

  public static getTitleBasedHtmlFileName(title: string): string {
    return `${title}.html`;
  }

  public static getTitleBasedPdfFileName(title: string): string {
    return `${title}.pdf`;
  }

  public static getVersionBasedPdfFileName(version: number, isPreview?: boolean): string {
    return `${InspectionReport._getVersionBasedFileName(version, isPreview)}.pdf`;
  }

  public static getVersionBasedHtmlFileName(version: number, isPreview?: boolean): string {
    return `${InspectionReport._getVersionBasedFileName(version, isPreview)}.html`;
  }

  private static _getVersionBasedFileName(version: number, isPreview?: boolean): string {
    return `${isPreview ? "draft" : "report"}-${version}`;
  }
}

export class InspectionReportMetaData {
  reportCount: number;
  reportDetails: ReportDetail[];
  inspectionId: string;
}

export class ReportDetail {
  reportId: string;
  status: string;
  title: string;
  lastUpdateDate: string;
}

export class ReportLocation {
  identifier: string;
  imageIds: string[];

  constructor(identifier: string) {
    this.identifier = identifier;
    this.imageIds = [];
  }
}

export class InspectionReportNew {
  title: string;
  templateId: string;
  inspectionIds: string[];
  reportLocations: ReportLocation[];
  isAutoInputTitle: boolean;
  schemaVersion: string;
  isSelectedImage: boolean;
  status: string;
}

export class InspectionReportUpdate {
  reportId: string;
  title: string;
  templateId: string;
  inspectionIds: string[];
  reportLocations: ReportLocation[];
  editableTexts: {};
  excludedInputGroups: string[];
  status: string;
  isAutoInputTitle: boolean;
  schemaVersion: string;
  finalQuality?: ReportImageQuality;
  draftQuality?: ReportImageQuality;
}

export class InspectionReportUpdateLocations {
  reportId: string;
  reportLocations: ReportLocation[];
}

export class InspectionReportUpdateTexts {
  reportId: string;
  editableTexts: {};
  excludedInputGroups: string[];
}

export class InspectionReportList {
  total: number;
  page: number;
  items: InspectionReportListItem[];
}

export class InspectionReportListItem {
  reportId: string;
  title: string;
  status: string;
  permission: number;
  lastUpdateDate: string;
  inspection: InspectionListItem;
  createdBy: string;
  thumbFilePath: string;
}

export class InspectionReportPdfUrl {
  url: string;
}
export class InspectionReportUri {
  sasUri: string;
}
export class InspectionReportReuse {
  reportId: string;
  reuseReportId: string;
  reuseReportVersion: number;

  constructor(targetReportId: string, reuseReportId: string, reuseReportVersion: number) {
    this.reportId = targetReportId;
    this.reuseReportId = reuseReportId;
    this.reuseReportVersion = reuseReportVersion;
  }
}

export class PdfOptions {
  dpi: number;
  imageDpi: number;
  quality: number;
  imageQuality: number;
  PageSize: string;
}

export class CreatePdfBodyRequest {
  pdfOptions: PdfOptions;
  previewPdfHtml: string;
}

export class ReportStatus {
  reportId: string;
  status: string;
  pdfOptions: PdfOptions;
  toPDFInstanceId?: string;
}

export class ReportTimer {
  reportId: string;
  timer: NodeJS.Timeout;
}

export class PastReportVersion {
  reportId: string;
  version: number;
  title: string;
  status: string;
}

export class GeneratedReport extends PastReportVersion {
  report: InspectionReportListItem;
  revisions: GeneratedReport[];
  isParent: boolean;

  constructor(reportId: string = null, version: number = null, title: string = null, status: string = null) {
    super();
    this.reportId = reportId;
    this.version = version;
    this.title = title;
    this.status = status;
    this.isParent = false;
  }
  setData(report: InspectionReportListItem, revisions: GeneratedReport[], version: number) {
    this.report = report;
    this.revisions = revisions;
    this.version = version;
    this.isParent = true;

    if (this.report) {
      this.reportId = this.report.reportId;
      this.title = this.report.title;
      this.status = this.report.status;
    }
  }
}

export class IndividualReportForOffline {
  id: string;
  tenantId: string;
  isDeleted: boolean;
  isValid: boolean;
  schemaVersion: string;
  createdBy: string;
  createDate: string;
  lastUpdatedBy: string;
  lastUpdateDate: string;
  reportId: string;
  title: string;
  version: number;
  templateId: string;
  status: string;
  editableTexts: any;
  excludedInputGroups: string[];
  reportLocations: ReportLocation[];
  isNewestVersion: boolean;
  type: string;
  blobStorageAccount: string;
  lastCompleteDate: string;
  lastDraftDate: string;
  inspectionIds: string[];
  draftReportQuality: ReportImageQuality;
  finalReportQuality: ReportImageQuality;
  finalReportTitle: string | null;

  constructor(schema: IndividualReportSchema) {
    this.id = schema.id;
    this.tenantId = schema.tenantId;
    this.isDeleted = schema.isDeleted;
    this.isValid = schema.isValid;
    this.schemaVersion = schema.schemaVersion;
    this.createdBy = schema.createdBy;
    this.createDate = schema.createDate;
    this.lastUpdatedBy = schema.lastUpdatedBy;
    this.lastUpdateDate = schema.lastUpdateDate;
    this.reportId = schema.reportId;
    this.title = schema.title;
    this.version = schema.version;
    this.templateId = schema.templateId;
    this.status = schema.status;
    this.editableTexts = JSON.parse(schema.editableTexts);
    this.excludedInputGroups = JSON.parse(schema.excludedInputGroups);
    this.reportLocations = JSON.parse(schema.reportLocations);
    this.isNewestVersion = schema.isNewestVersion;
    this.type = schema.type;
    this.blobStorageAccount = schema.blobStorageAccount;
    this.lastCompleteDate = null;
    this.lastDraftDate = null;
    this.inspectionIds = [schema.inspectionId];
    this.draftReportQuality = schema.draftReportQuality;
    this.finalReportQuality = schema.finalReportQuality;
    this.finalReportTitle = InspectionReport.getFinalReportTitle(this.title, this.version);
  }
}

export class InspectionReportPdfStatus {
  instanceId: string;
  status: string;
  result: InspectionReportPdfUrl;
}

export class InspectionReportInputGroup {
  name: string;
  isSelected: boolean;
}
